import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, MagnifyingGlass, Clock, User as UserIcon } from "@phosphor-icons/react";
import { createMovement, listMovements, listProducts } from "@/lib/api";
import Stepper from "@/components/Stepper";
import ProductPicker from "@/components/ProductPicker";
import { StatusPill } from "@/components/StatusPill";
import { toast } from "sonner";

export default function Movements() {
  const [picker, setPicker] = useState(false);
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [user, setUser] = useState(() => localStorage.getItem("stock_user") || "");
  const [note, setNote] = useState("");
  const [recent, setRecent] = useState([]);

  const loadRecent = () => listMovements({ limit: 30 }).then(setRecent);
  useEffect(() => { loadRecent(); }, []);

  useEffect(() => { localStorage.setItem("stock_user", user); }, [user]);

  const refreshProduct = async () => {
    if (!product) return;
    const all = await listProducts({ q: product.name });
    const fresh = all.find((p) => p.id === product.id);
    if (fresh) setProduct(fresh);
  };

  const submit = async (type) => {
    if (!product) {
      toast.error("Sélectionnez un produit");
      return;
    }
    if (!qty || qty <= 0) {
      toast.error("Quantité invalide");
      return;
    }
    try {
      await createMovement({ product_id: product.id, type, quantity: Number(qty), user: user || null, note: note || null });
      toast.success(type === "IN" ? `+${qty} ${product.unit} enregistrées` : `−${qty} ${product.unit} enregistrées`);
      setNote("");
      setQty(1);
      await refreshProduct();
      loadRecent();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="fade-up space-y-6">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] font-bold text-[color:var(--text-muted)]">Saisie terrain</div>
          <h1 className="text-4xl font-heading font-medium mt-1">Mouvement de produits</h1>
        </div>
        <div className="flex items-center gap-2 bg-white border border-[color:var(--border-soft)] rounded-2xl px-4 h-12">
          <UserIcon size={20} className="text-[color:var(--text-muted)]" />
          <input
            data-testid="input-user"
            placeholder="Votre nom (optionnel)"
            value={user}
            onChange={(e) => setUser(e.target.value)}
            className="w-48 bg-transparent focus:outline-none text-sm"
          />
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Carte saisie */}
        <div className="lg:col-span-3 bg-white rounded-3xl border border-[color:var(--border-soft)] p-6 md:p-8">
          <div className="text-xs uppercase tracking-[0.1em] font-bold text-[color:var(--text-muted)]">Produit</div>
          <button
            onClick={() => setPicker(true)}
            data-testid="open-picker"
            className="mt-2 w-full min-h-[88px] rounded-2xl border-2 border-dashed border-[color:var(--border-soft)] px-5 py-4 flex items-center gap-4 hover:border-[color:var(--brand)] hover:bg-[color:var(--brand-muted)] press-scale text-left"
          >
            {product ? (
              <>
                <div className="flex-1">
                  <div className="text-xl font-medium text-[color:var(--text-primary)]">{product.name}</div>
                  <div className="text-sm text-[color:var(--text-muted)] flex gap-2 mt-1 flex-wrap">
                    <span>{product.category}</span>
                    <span>·</span>
                    <span>Stock : <b>{product.stock}</b> {product.unit}</span>
                    {product.supplier_name && <><span>·</span><span>{product.supplier_name}</span></>}
                  </div>
                </div>
                {product.status && <StatusPill status={product.status} size="lg" />}
              </>
            ) : (
              <>
                <MagnifyingGlass size={32} className="text-[color:var(--text-muted)]" />
                <div>
                  <div className="text-lg font-medium">Choisir un produit</div>
                  <div className="text-sm text-[color:var(--text-muted)]">Recherche, liste, ou créer un nouveau produit</div>
                </div>
              </>
            )}
          </button>

          <div className="mt-8">
            <div className="text-xs uppercase tracking-[0.1em] font-bold text-[color:var(--text-muted)] mb-4">Quantité</div>
            <div className="flex justify-center">
              <Stepper value={qty} onChange={setQty} step={1} min={0} testid="qty" />
            </div>
          </div>

          <div className="mt-6">
            <label className="block">
              <span className="text-xs uppercase tracking-[0.1em] font-bold text-[color:var(--text-muted)] block mb-1.5">Note (optionnel)</span>
              <input
                data-testid="input-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Lot, livraison, salle…"
                className="w-full h-14 px-4 rounded-2xl border border-[color:var(--border-soft)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]"
              />
            </label>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4">
            <button
              onClick={() => submit("OUT")}
              data-testid="btn-out"
              disabled={!product}
              className="h-20 rounded-3xl bg-[#FCE4E4] text-[#822222] font-semibold text-lg flex items-center justify-center gap-3 press-scale hover:bg-[#F5D0D0] disabled:opacity-50"
            >
              <ArrowDown size={28} weight="bold" /> Sortie
            </button>
            <button
              onClick={() => submit("IN")}
              data-testid="btn-in"
              disabled={!product}
              className="h-20 rounded-3xl bg-[color:var(--brand)] text-white font-semibold text-lg flex items-center justify-center gap-3 press-scale hover:bg-[color:var(--brand-hover)] disabled:opacity-50"
            >
              <ArrowUp size={28} weight="bold" /> Entrée
            </button>
          </div>
        </div>

        {/* Historique */}
        <div className="lg:col-span-2 bg-white rounded-3xl border border-[color:var(--border-soft)] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock size={22} className="text-[color:var(--brand)]" />
            <div className="font-heading text-lg font-medium">Historique récent</div>
          </div>
          <ul className="space-y-2 max-h-[620px] overflow-y-auto pr-1" data-testid="history-list">
            {recent.length === 0 && <li className="text-sm text-[color:var(--text-muted)] py-8 text-center">Pas encore de mouvement.</li>}
            {recent.map((m) => (
              <li key={m.id} className="flex items-center gap-3 p-3 rounded-2xl hover:bg-[color:var(--bg)]">
                <span className={`w-11 h-11 rounded-xl flex items-center justify-center font-bold text-lg ${m.type === "IN" ? "status-ok" : m.type === "OUT" ? "status-critical" : "bg-[color:var(--brand-muted)] text-[color:var(--brand)]"}`}>
                  {m.type === "IN" ? "+" : m.type === "OUT" ? "−" : "="}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{m.product_name}</div>
                  <div className="text-xs text-[color:var(--text-muted)] flex gap-2 flex-wrap">
                    <span>{new Date(m.created_at).toLocaleString("fr-FR")}</span>
                    {m.user && <><span>·</span><span>{m.user}</span></>}
                    {m.note && <><span>·</span><span className="italic truncate max-w-[120px]">{m.note}</span></>}
                  </div>
                </div>
                <div className="text-base font-semibold tabular-nums">{m.type === "ADJUST" ? `= ${m.quantity}` : m.quantity}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {picker && (
        <ProductPicker
          onPick={(p) => { setProduct(p); setPicker(false); }}
          onClose={() => setPicker(false)}
          selectedId={product?.id}
        />
      )}
    </div>
  );
}
